import {useState, useEffect, type Dispatch, type SetStateAction, type MutableRefObject} from 'react';
import {
  type ExportImport,
  type NavigationTreePlan,
  type Plan,
  type WritePlan,
  type WritePlanUsingProductionHistory,
} from 'client';
import useIsMounted from 'hooks/useIsMounted';

import backendCommunicator from 'services/backendCommunicator';
import {useInputMenuContext} from 'context/InputMenu';

import {inputMenuActionType, methodType, URLPathname, SWRKeyEnum} from 'interfaces';
import {type AxiosError} from 'axios';

import Alert from 'helpers/Alert';
import {addDays, localDateSender, dateReader} from 'helpers/UnitsFormatHandler';
import useParseURL from 'hooks/useParseURL';

import {mutate} from 'swr';
import {EMPTY_ID, useGetPlan} from 'hooks/swr';
import refreshHandler, {mutateSites} from 'helpers/refreshHandler';
import {useTranslation} from 'react-i18next';
import {type TFunction} from 'i18next';
import useGetPlanTreeNavigation from 'hooks/plan/useGetPlanTreeNavigation';
import {useSnackbar} from 'notistack';
import {planStatusSnackbarActionFactory} from 'hooks/usePlanOptimization';
import useGetLongTermRange from 'hooks/useGetLongTermRange';

interface initialPlanCreate {
  startDate: Date | null;
  endDate: Date | null;
  name: string;
  usingHistoricalData: boolean;
  description: string | null;
  tags?: string[];
  folderId?: string | null;
}

const initialState = (t: TFunction, selectedPlanState?: Plan, method?: methodType): initialPlanCreate => {
  if (selectedPlanState) {
    return {
      startDate: dateReader(selectedPlanState.startDate),
      endDate: dateReader(selectedPlanState.endDate),
      name: method === methodType.copy ? `${selectedPlanState.name} ${t('copy')}` : selectedPlanState.name,
      usingHistoricalData: false,
      description: selectedPlanState?.description ?? '',
      tags: selectedPlanState.tags ?? [],
      folderId: selectedPlanState.folderId ?? '',
    };
  } else {
    const startDate = new Date();
    const fourYears = 4 * 365;
    const endDate = addDays(startDate, fourYears);
    return {
      startDate,
      endDate,
      name: '',
      description: '',
      folderId: '',
      tags: [],
      usingHistoricalData: false,
    };
  }
};

const planToCreate = (
  plan: initialPlanCreate,
  setCreatePlan: Dispatch<SetStateAction<boolean>>,
  isMounted: MutableRefObject<boolean>,
  t: TFunction,
) => {
  if (plan.name.length === 0) {
    Alert.warning(t('planNameError'));
    isMounted.current && setCreatePlan(false);
  } else if (plan.startDate && plan.endDate && plan.startDate.getTime() >= plan.endDate.getTime()) {
    Alert.warning(t('startDateEndDateError'));
    isMounted.current && setCreatePlan(false);
  } else if (plan.startDate === null || plan.endDate === null) {
    Alert.warning(t('startDateEndDateError'));
  } else {
    return {
      name: plan.name,
      description: plan.description,
      tags: plan.tags,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      folderId: plan.folderId || null,
      startDate: localDateSender(plan.startDate),
      endDate: localDateSender(plan.endDate),
    };
  }
};

const backendOperator = async (
  method: methodType,
  usingHistoricalData: boolean,
  plan?: WritePlan | WritePlanUsingProductionHistory | false,
  planId?: string,
  originalPlan?: Plan,
) => {
  if (method === methodType.copy && planId && plan) {
    return await backendCommunicator.copyPlan({planId, ...plan});
  } else if (method === methodType.add && plan) {
    if (usingHistoricalData) {
      return await backendCommunicator.createPlanUsingProductionHistory(plan);
    } else {
      return await backendCommunicator.createPlan(plan);
    }
  } else if (method === methodType.edit && planId && plan) {
    const originalEndDate = originalPlan?.endDate;
    if (plan.endDate && originalEndDate === plan.endDate) {
      // @ts-expect-error No idea why
      delete plan.endDate;
    }
    return await backendCommunicator.updatePlan(planId, plan);
  }
};

function useAddEditCopyPlan({importPlan}: {importPlan?: ExportImport}) {
  const {planId, navigate} = useParseURL();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);

  const {t} = useTranslation('common');

  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const inputMenuState = inputMenuContext?.state;

  const {data: formPlan} = useGetPlan({resourceId: inputMenuState?.formData?.planId});
  const {startDate, endDate} = useGetLongTermRange();
  const {planTreeNavigation} = useGetPlanTreeNavigation({planId, isMounted});

  useEffect(() => {
    const initialPlan = initialState(t, formPlan, inputMenuState?.method);
    setPlan(initialPlan);
  }, [formPlan]);

  const [plan, setPlan] = useState<initialPlanCreate | undefined>();
  const [createPlan, setCreatePlan] = useState<boolean>(false);

  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const openToast = (planId: string, message: string) =>
    enqueueSnackbar(message, {
      persist: true,
      action: planStatusSnackbarActionFactory({
        onClick: () => {
          closeSnackbar();
          navigate({
            pathname: URLPathname.planOverview,
            search: new URLSearchParams({plan: planId}).toString(),
          });
        },
        text: t('gotoNewPlan'),
        closeSnackbar,
        dismissText: t('dismiss'),
      }),
      variant: 'success',
    });

  const handleCommunicationSuccess = (
    planId: string,
    URLPlan: string | null,
    method?: methodType,
    planTreeNavigation?: NavigationTreePlan,
  ) => {
    void mutate([planId, SWRKeyEnum.GET_PLAN]);
    void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
    void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
    inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
    if (!!planId.length && URLPlan !== planId) {
      if (method === methodType.add) {
        openToast(planId, t(`newPlanCreatedToast`));
      } else if (method === methodType.copy) {
        openToast(planId, t(`newPlanCopiedToast`));
      }
    }

    if (method === methodType.edit && planTreeNavigation && startDate && endDate) {
      refreshHandler({
        planId,
        startDate,
        endDate,
        filteredSites: [],
      });

      startDate && endDate && mutateSites(planTreeNavigation, startDate, endDate);
    }
  };

  useEffect(() => {
    const _plan = plan && importPlan === undefined && createPlan && planToCreate(plan, setCreatePlan, isMounted, t);
    const _planId = inputMenuState?.formData?.planId ?? planId;
    const backendOperation =
      plan &&
      inputMenuState?.method &&
      backendOperator(inputMenuState?.method, plan.usingHistoricalData, _plan, _planId, formPlan);

    if (backendOperation) {
      setIsLoading(true);
      backendOperation
        .then((response) => {
          if (response) {
            handleCommunicationSuccess(response.data.id, planId, inputMenuState?.method, planTreeNavigation);
          }
        })
        .catch((error: AxiosError) => {
          isMounted.current && Alert.error(error);
        })
        .finally(() => {
          setIsLoading(false);
          if (isMounted.current) {
            setCreatePlan(false);
          }
        });
    }
  }, [createPlan]);

  return {
    plan,
    setPlan,
    createPlan,
    setCreatePlan,
    inputMenuState,
    inputMenuDispatch,
    isMounted,
    isLoading,
    setIsLoading,
    openToast,
  };
}

export default useAddEditCopyPlan;
